import { HttpParams } from '@angular/common/http';
import { Component, DestroyRef, Input, Signal, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { catchError, delay, map, of, switchMap } from 'rxjs';
import { DnsRecord, PullZone } from '@bn/api-interfaces';
import { DnsApiService } from '@bn/dashboard-shared/services/dns';
import { DnsRecordType } from '@bn/shared/entities/enums';
import { StringTMap } from '@bn/shared/entities/models';
import { PullZoneStore, StoreRequest } from '@bn/shared/state-store';
import { AlertComponent } from '@bn/web-ui/alert/alert.component';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { AbstractDialogDirective } from '@bn/web-ui/dialog/abstracts';
import { InputComponent } from '@bn/web-ui/input/input.component';

interface DnsResponseMessage {
  success: boolean;
  text: string;
}

@Component({
  selector: 'bn-dashboard-verify-ssl-hostname-modal',
  imports: [AlertComponent, ButtonComponent, FaIconComponent, FormsModule, InputComponent, ReactiveFormsModule, RouterLink],
  templateUrl: './verify-ssl-hostname-modal.component.html',
})
export class VerifySSLHostnameModalComponent extends AbstractDialogDirective {
  bsModalRef = inject(BsModalRef);

  @Input()
  public set dataItems(items: StringTMap<string>[]) {
    if (!items || !items.length) return;

    this.setDefaultValues(items);
  }

  @Input()
  public set pullZoneId(cdnId: number | undefined) {
    if (!cdnId) return;

    this.internalPullZoneId = cdnId;
  }

  readonly #pullZoneStore = inject(PullZoneStore);
  readonly #dnsApiService = inject(DnsApiService);

  public getSelectedPullZone: Signal<PullZone | undefined> = this.#pullZoneStore.selectedEntity();
  public isLoadingFreeSSL = this.#pullZoneStore.isLoading(StoreRequest.Update, ['LoadFreeSSL']);
  public isFinishedLoadFreeSSL = this.#pullZoneStore.isFinished(StoreRequest.Update, ['LoadFreeSSL']);
  public getErrorLoadFreeSSL = this.#pullZoneStore.getError(StoreRequest.Update, ['LoadFreeSSL']);
  public isFinishedCheckDns = this.#pullZoneStore.isFinished(StoreRequest.Update, ['CheckDns']);
  public getErrorCheckDns = this.#pullZoneStore.getError(StoreRequest.Update, ['CheckDns']);
  public isLoadingCheckDns = this.#pullZoneStore.isLoading(StoreRequest.Update, ['CheckDns']);

  public hostname = '';
  public value = '';
  public internalPullZoneId: PullZone['Id'] = 0;
  public pullZone = computed(() => this.getSelectedPullZone());
  public dnsCheckError = false;
  public dnsCheckState: 'loading' | 'auto-configured' | 'cname-error' | 'cname-wrong-value' | 'manual' = 'loading';
  public foundDnsZoneId?: number;
  public showInstructions = false;
  public responseMessage: DnsResponseMessage = {
    success: false,
    text: '',
  };

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();

    this.subscribeToStoreEvents();
  }

  protected onDataInit(): void {
    this.internalPullZoneId = (this.data?.parentId as number) ?? 0;

    const dataItems = this.data?.items as StringTMap<string>[];

    this.setDefaultValues(dataItems);
  }

  public verifySSL(): void {
    this.#pullZoneStore.checkDns({
      id: this.internalPullZoneId,
      hostname: this.hostname,
    });
  }

  public forceActivateSSL(): void {
    this.#pullZoneStore.loadFreeSSLCertificate({
      id: this.internalPullZoneId,
      hostname: this.hostname,
    });
  }

  private setDefaultValues(dataItems: StringTMap<string>[]): void {
    this.hostname = dataItems.find((item) => item.id === 'hostname')?.value ?? '';
    this.value = dataItems.find((item) => item.id === 'cname')?.value || '';

    if (this.hostname && this.value) {
      this.checkAndAutoConfigure();
    }
  }

  private checkAndAutoConfigure(): void {
    this.dnsCheckState = 'loading';

    const cleanHostname = this.hostname.startsWith('*.') ? this.hostname.slice(2) : this.hostname;
    const parts = cleanHostname.split('.');
    const searchDomain = parts.length >= 2 ? parts.slice(-2).join('.') : cleanHostname;

    this.#dnsApiService
      .getDnsZonesList(new HttpParams().set('search', searchDomain), true)
      .pipe(
        switchMap(({ Items }) => {
          const zone = Items.filter((z) => z.NameserversDetected && (cleanHostname === z.Domain || cleanHostname.endsWith('.' + z.Domain))).sort(
            (a, b) => b.Domain.length - a.Domain.length,
          )[0];

          if (!zone) return of('manual' as const);

          this.foundDnsZoneId = zone.Id;

          const recordName = this.hostname === zone.Domain ? '@' : this.hostname.slice(0, -(zone.Domain.length + 1));

          return this.#dnsApiService.getDnsZone(zone.Id).pipe(
            switchMap((fullZone) => {
              const existing = fullZone.Records.find((r) => r.Type === DnsRecordType.CNAME && r.Name === recordName);

              if (existing) {
                return of(existing.Value === this.value ? ('auto-configured' as const) : ('cname-wrong-value' as const));
              }

              const record: Partial<DnsRecord> = { Name: recordName, Type: DnsRecordType.CNAME, Value: this.value, Ttl: 3600 };

              return this.#dnsApiService.createDnsRecord({ parentEntityId: zone.Id }, record, undefined, true).pipe(
                map(() => 'auto-configured' as const),
                catchError(() => of('cname-error' as const)),
              );
            }),
            catchError(() => of('cname-error' as const)),
          );
        }),
        catchError(() => of('manual' as const)),
        delay(500),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => {
        this.dnsCheckState = state;
      });
  }

  private subscribeToStoreEvents(): void {
    toObservable(this.isFinishedCheckDns)
      .pipe(takeUntilDestroyed())
      .subscribe((isFinishedCheckDns) => {
        if (!isFinishedCheckDns) return;

        this.#pullZoneStore.loadFreeSSLCertificate({
          id: this.internalPullZoneId,
          hostname: this.hostname,
        });
      });

    toObservable(this.isFinishedLoadFreeSSL)
      .pipe(takeUntilDestroyed())
      .subscribe((isFinishedLoadFreeSSL) => {
        if (!isFinishedLoadFreeSSL) return;

        this.onAction.next({
          action: 'save',
          itemId: this.internalPullZoneId,
          data: { entity: this.pullZone(), name: this.hostname },
        });

        this.responseMessage = {
          success: true,
          text: 'Hostname verified successfully',
        };
      });

    toObservable(this.getErrorLoadFreeSSL)
      .pipe(takeUntilDestroyed())
      .subscribe((error) => {
        if (!error) return;

        this.responseMessage = {
          success: false,
          text: error.error?.Message ?? '',
        };
      });

    toObservable(this.getErrorCheckDns)
      .pipe(takeUntilDestroyed())
      .subscribe((error) => {
        if (!error) return;

        this.dnsCheckError = true;

        this.responseMessage = {
          success: false,
          text: error.error?.Message ?? '',
        };
      });
  }
}
